<template>
  <div>
    <!-- Sending is real. The blast radius is capped server-side by
         NOTIFICATION_TEST_RECIPIENTS, and the operator has to be told that
         here, not only in the receipt after they have pressed Send. -->
    <v-alert dense text type="warning" class="kf-note">
      <strong>Sending is live.</strong> This environment holds real WhatsApp, SMS
      and Firebase credentials. Messages go only to the addresses on the server's
      test allowlist — everyone else in the segment is reported as skipped.

    </v-alert>

    <v-row dense class="mb-1">
      <v-col cols="6" sm="3">
        <KpiCard label="Sent" :value="summary.total" icon="send" color="#1976D2" :loading="loading" />
      </v-col>
      <v-col cols="6" sm="3">
        <KpiCard label="Delivered" :value="summary.delivered" icon="circle-check" color="#2E7D32" :loading="loading" :sub="fmt.percent(summary.delivery_rate)" />
      </v-col>
      <v-col cols="6" sm="3">
        <KpiCard label="Failed" :value="summary.failed" icon="circle-alert" color="#C62828" :loading="loading" />
      </v-col>
      <v-col cols="6" sm="3">
        <!-- Was a hardcoded 4 with a hardcoded caption, so it kept saying four
             even when a channel had never been used or a fifth was added. Both
             halves now come off summary.by_channel. -->
        <KpiCard label="Channels" :value="channelsUsed" icon="megaphone" color="#194E76" :loading="loading" :sub="channelsUsedSub" />
      </v-col>
    </v-row>

    <v-row dense>
      <!-- ------------------------------------------------- compose panel -->
      <v-col cols="12" md="5">
        <div class="kf-card neumorphism-flat">
          <div class="kf-card__title">Compose</div>
          <div class="kf-card__hint">
            Pick a channel and an audience, then preview before sending.
          </div>

          <div class="kf-section">Channel</div>
          <div class="kf-channels">
            <button
              v-for="c in channels"
              :key="c.value"
              class="kf-channel"
              :class="{ 'kf-channel--active': form.channel === c.value }"
              @click="selectChannel(c.value)"
            >
              <LucideIcon
                :name="c.icon"
                :size="18"
                :color="form.channel === c.value ? '#fff' : channelColor(c.value)"
              />
              <span>{{ c.label }}</span>
            </button>
          </div>
          <div class="kf-hint-sm">
            RCS is not available — it does not exist in this platform.
          </div>

          <div class="kf-section">Audience</div>

          <!-- Segment or named people. The two are exclusive by design: the
               server treats named recipients as overriding every segment
               filter, so showing both sets of controls at once would imply an
               intersection that does not happen. -->
          <v-btn-toggle
            v-model="form.mode"
            mandatory dense
            class="kf-mode mb-3"
            color="primary"
          >
            <v-btn small value="segment" class="text-none">
              <LucideIcon name="users" :size="15" class="mr-1" />
              Segment
            </v-btn>
            <v-btn small value="individual" class="text-none">
              <LucideIcon name="user" :size="15" class="mr-1" />
              Individual
            </v-btn>
          </v-btn-toggle>

          <template v-if="!isIndividual">
            <ProductSelect class="mb-2" @change="loadAudience" />
            <v-select
              v-model="form.bucket"
              :items="buckets"
              label="Lead bucket"
              hide-details dense outlined clearable
              class="mb-2"
              @change="loadAudience"
            />
            <v-select
              v-model="form.utm_source"
              :items="utmSources"
              label="UTM source (optional)"
              hide-details dense outlined clearable
              class="mb-2"
              @change="loadAudience"
            />
          </template>

          <template v-else>
            <!-- `return-object` so a chosen row keeps the name, the contact and
                 the two reachability flags. Binding ids alone would leave the
                 list below showing raw numbers the moment the search results
                 changed. -->
            <v-autocomplete
              v-model="recipients"
              :items="recipientItems"
              :loading="recipientLoading"
              :search-input.sync="recipientSearch"
              item-text="name"
              item-value="id"
              label="Search name, mobile or email"
              :placeholder="recipientPlaceholder"
              persistent-placeholder
              prepend-inner-icon="mdi-magnify"
              multiple return-object clearable
              hide-no-data
              no-filter
              dense outlined
              hide-details
              class="kf-recip-search mb-2"
              @change="onRecipientsChange"
            >
              <!-- Nothing is drawn inside the box, on purpose.
               *
               * `chips` put a 32px pill in a 40px dense field, and the floating
               * label sits 9px INTO that field -- so the pill covered the word
               * "Search". Vuetify's fallback when the chips go is a comma list,
               * which would cover the placeholder instead. The chosen people
               * get their own list below, which also has room for the mobile
               * number that tells two same-named customers apart.
               *
               * Dropping `chips` has a second effect worth keeping: Vuetify
               * collapses the typing area of a chip multi-select to zero height
               * until it is focused, which is why the placeholder had never
               * been visible on this screen. -->
              <template #selection>
                <span class="d-none"></span>
              </template>

              <!-- The dropdown row carries what the field cannot: who this is,
                   and whether a message would actually get to them. -->
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <!-- `display_name` falls back to the mobile, so for a customer
                       with no name on file the subtitle would print the same
                       number a second time. -->
                  <v-list-item-subtitle v-if="item.mobile !== item.name || item.email">
                    <span v-if="item.mobile !== item.name">{{ item.mobile }}</span>
                    <span v-if="item.email">
                      <template v-if="item.mobile !== item.name"> · </template>{{ item.email }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <!-- A green tick and a red slash with nothing to read them by
                       are two colours; the tooltip is what makes them a fact. -->
                  <v-tooltip left>
                    <template #activator="{ on, attrs }">
                      <span class="d-inline-flex" v-bind="attrs" v-on="on">
                        <LucideIcon
                          :name="item.reachable ? 'circle-check' : 'circle-slash'"
                          :size="16"
                          :color="item.reachable ? '#2E7D32' : '#C62828'"
                        />
                      </span>
                    </template>
                    <span v-if="item.reachable">Reachable on {{ channelLabel }}</span>
                    <span v-else>Cannot be reached — {{ unreachableReason }}</span>
                  </v-tooltip>
                </v-list-item-action>
              </template>
            </v-autocomplete>

            <!-- The chosen people, listed rather than pilled. A search for
                 "Priya" returns three of them, and a name-only chip cannot say
                 which one was picked; the height is capped so naming ten people
                 scrolls here instead of pushing Send off the screen. -->
            <div v-if="recipients.length" class="kf-recips mb-2">
              <div class="kf-recips__head">
                <span>{{ recipients.length }} selected</span>
                <button class="kf-recips__clear" @click="clearRecipients">
                  Clear all
                </button>
              </div>
              <div class="kf-recips__list">
                <div
                  v-for="r in recipients"
                  :key="r.id"
                  class="kf-recips__row"
                  :class="{ 'kf-recips__row--warn': recipientProblem(r) }"
                >
                  <div class="kf-recips__who">
                    <span class="kf-recips__name">{{ r.name }}</span>
                    <span v-if="recipientContact(r)" class="kf-recips__contact">
                      {{ recipientContact(r) }}
                    </span>
                  </div>
                  <span v-if="recipientProblem(r)" class="kf-recips__status">
                    {{ recipientProblem(r) }}
                  </span>
                  <button
                    class="kf-recips__remove"
                    :aria-label="'Remove ' + r.name"
                    @click="removeRecipient(r)"
                  >
                    <LucideIcon name="x" :size="14" color="#54636f" />
                  </button>
                </div>
              </div>
            </div>

            <div class="kf-hint-sm mb-2">
              Named people are messaged directly — the bucket, product and UTM
              filters do not apply.
            </div>
          </template>

          <div class="kf-audience">
            <div class="kf-audience__row">
              <span>{{ isIndividual ? "Selected people" : "Matching users" }}</span>
              <strong>{{ audienceLoading ? "…" : fmt.number(audience.total) }}</strong>
            </div>
            <div class="kf-audience__row kf-audience__row--sub">
              <span>Reachable on {{ channelLabel }}</span>
              <strong>{{ audienceLoading ? "…" : fmt.number(reachable) }}</strong>
            </div>
            <div v-if="!audienceLoading && reachable < audience.total" class="kf-audience__note">
              {{ reachGapNote }}
            </div>
            <!-- One line, not two lists of names. Each person's own reason now
                 sits on their row in the list above, and the two sentences this
                 replaces repeated every name a second time -- unreadable past
                 three recipients. It still has to be said before Send, which is
                 the only point at which the operator can fix it. -->
            <div v-if="isIndividual && undeliverableCount" class="kf-audience__note">
              <strong>{{ undeliverableCount }} of {{ recipients.length }}</strong>
              won't receive this — the reason is on each row above.
            </div>
          </div>

          <div class="kf-section">Message</div>
          <!-- WhatsApp AND SMS pick a template. Both are provider-registered
               channels: Meta must have approved a WhatsApp template, and MSG91
               rejects an SMS body that is not on the DLT register. Only Push
               and Email are free text. -->
          <v-select
            v-if="usesTemplate"
            v-model="form.template"
            :items="templateItems"
            :label="form.channel === 'SMS' ? 'DLT template *' : 'Template *'"
            :hint="templateHint"
            persistent-hint
            dense outlined
            class="mb-2"
          />
          <template v-else>
            <v-text-field
              v-model="form.title"
              label="Title *"
              hide-details dense outlined
              class="mb-2"
            />
            <v-textarea
              v-model="form.body"
              label="Message *"
              rows="3"
              hide-details dense outlined
              class="mb-2"
              counter
            />
          </template>

          <v-btn
            block
            color="primary"
            depressed
            class="text-none mt-3"
            :disabled="!canSend"
            @click="openPreview"
          >
            <LucideIcon name="eye" :size="18" class="mr-1" />
            Preview &amp; send
          </v-btn>
        </div>
      </v-col>

      <!-- --------------------------------------------------- history -->
      <v-col cols="12" md="7">
        <div class="kf-card neumorphism-flat mb-3">
          <div class="kf-card__title">Delivery breakdown</div>
          <v-skeleton-loader v-if="loading" type="image" />
          <div v-else class="kf-breakdown mt-2">
            <div v-for="s in summary.by_status" :key="s.label" class="kf-bd">
              <div class="kf-bd__top">
                <StatusChip :status="s.label" small />
                <span class="kf-bd__value">{{ fmt.number(s.value) }}</span>
              </div>
              <div class="kf-bd__bar">
                <div
                  class="kf-bd__fill"
                  :style="{ width: statusWidth(s.value), backgroundColor: s.color }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <DataTable
          title="Sent history"
          :headers="headers"
          :items="items"
          :loading="loading"
          :error="error"
          :page="page"
          :total-pages="totalPages"
          :total-count="totalCount"
          :page-length="pageLength"
          empty-title="Nothing sent yet"
          empty-message="No notifications match these filters."
          empty-icon="bell-off"
          @page-change="onPage"
          @retry="load"
        >
          <template #actions>
            <v-select
              v-model="filter_channel"
              :items="channelFilterItems"
              label="Channel"
              hide-details dense outlined clearable
              class="kf-inline-select"
              @change="reload"
            />
          </template>

          <template #[`item.name`]="{ item }">
            <div>
              <div class="kf-user__name">{{ item.name }}</div>
              <div class="kf-user__sub">{{ item.contact }}</div>
            </div>
          </template>

          <template #[`item.channel`]="{ item }">
            <span class="kf-ch">
              <LucideIcon :name="channelIcon(item.channel)" :size="14" :color="channelColor(item.channel)" />
              {{ item.channel }}
            </span>
          </template>

          <template #[`item.status`]="{ item }">
            <StatusChip :status="item.status" small />
          </template>

          <template #[`item.sent_at`]="{ item }">
            <span :title="fmt.dateTime(item.sent_at)">{{ fmt.relative(item.sent_at) }}</span>
          </template>

          <template #[`item.trigger`]="{ item }">
            <span v-if="item.trigger === 'manual'" class="kf-muted">Manual</span>
            <span v-else class="kf-auto">{{ item.trigger.replace(/_/g, " ") }}</span>
          </template>
        </DataTable>
      </v-col>
    </v-row>

    <!-- ----------------------------------------------------- preview -->
    <ConfirmDialog
      v-model="preview_dialog"
      title="Preview notification"
      confirm-text="Send"
      icon="send"
      :loading="sending"
      @cancel="preview_dialog = false"
      @confirm="send"
    >
      <div class="kf-preview">
        <div class="kf-preview__head">
          <LucideIcon :name="channelIcon(form.channel)" :size="16" :color="channelColor(form.channel)" />
          <span>{{ channelLabel }}</span>
        </div>
        <div class="kf-preview__body">
          <template v-if="usesTemplate">
            <div class="kf-preview__title">{{ templateLabel }}</div>
            <div class="kf-preview__text">
              Template <code>{{ form.template }}</code> will be rendered with each
              user's own name and offer details.
            </div>
          </template>
          <template v-else>
            <div class="kf-preview__title">{{ form.title }}</div>
            <div class="kf-preview__text">{{ form.body }}</div>
          </template>
        </div>
      </div>

      <!-- Named recipients are read back by name. Confirming "Going to 1 user"
           before messaging a specific person tells the operator nothing about
           whether it is the RIGHT person. -->
      <p v-if="isIndividual" class="mt-3 mb-1">
        Going to <strong>{{ recipientSummary }}</strong>.
      </p>
      <p v-else class="mt-3 mb-1">
        Going to <strong>{{ fmt.number(reachable) }}</strong> user{{ reachable === 1 ? "" : "s" }}
        <span v-if="form.bucket">in “{{ displayLabel(form.bucket) }}”</span>.
      </p>
      <p class="kf-warn mb-0">
        This sends for real. Only recipients on the server's test allowlist are
        messaged; the rest are counted as skipped.
      </p>
    </ConfirmDialog>
  </div>
</template>

<script>
import ProductSelect from "@/components/karrofin/ProductSelect.vue";
import KpiCard from "@/components/karrofin/KpiCard.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import ConfirmDialog from "@/components/karrofin/ConfirmDialog.vue";
import api from "@/app-js/karrofin/api";
import store from "@/app-js/karrofin/store";
import fmt from "@/app-js/karrofin/format";
import {
  CHANNELS,
  CHANNEL,
  LEAD_BUCKETS,
  WHATSAPP_TEMPLATES,
  SMS_TEMPLATES,
  displayLabel,
} from "@/app-js/karrofin/constants";
import { PAGE_LENGTH } from "@/app-js/karrofin/config";

/** The two ways an audience can be chosen. */
const MODE = {
  SEGMENT: "segment",
  INDIVIDUAL: "individual",
};

const CHANNEL_COLORS = {
  Push: "#1976D2",
  Whatsapp: "#25D366",
  SMS: "#F57C00",
  Email: "#7B1FA2",
};

export default {
  name: "KarroFinNotifications",
  components: {
    KpiCard,
    ProductSelect,
    DataTable,
    StatusChip,
    ConfirmDialog,
  },
  data() {
    return {
      fmt,
      loading: true,
      error: null,
      items: [],
      // by_channel included so the Channels KPI has the same shape before the
      // first response as after it -- normalise.notificationList guarantees both.
      summary: { total: 0, delivered: 0, failed: 0, delivery_rate: 0, by_status: [], by_channel: [] },
      page: 1,
      totalPages: 1,
      totalCount: 0,
      pageLength: PAGE_LENGTH,
      filter_channel: null,
      channels: CHANNELS,
      // {text, value}: the bucket reads by its plain name while the value
      // stays the one the audience query filters on.
      buckets: LEAD_BUCKETS.map((b) => ({ text: displayLabel(b), value: b })),
      utmSources: [],
      // Held per channel rather than as one list: a WhatsApp template name is
      // not a valid SMS template and vice versa, so switching channel has to
      // swap the whole dropdown, not filter it.
      templates: {
        [CHANNEL.WHATSAPP]: WHATSAPP_TEMPLATES.slice(),
        [CHANNEL.SMS]: SMS_TEMPLATES.slice(),
      },
      form: {
        channel: CHANNEL.WHATSAPP,
        // "segment" filters by bucket/product/UTM; "individual" names people.
        // Never both -- the server treats named recipients as overriding every
        // segment filter, so the two cannot be combined.
        mode: MODE.SEGMENT,
        bucket: LEAD_BUCKETS[0],
        utm_source: null,
        template: WHATSAPP_TEMPLATES[5].name,
        title: "",
        body: "",
      },
      /* The chosen people, as whole objects rather than ids: each carries the
         name for the chip, plus `reachable` and `allowlisted` for the warnings
         above the Send button. Both flags are per channel, so they are
         refreshed whenever the channel changes. */
      recipients: [],
      recipientItems: [],
      recipientSearch: null,
      recipientLoading: false,
      recipientTimer: null,
      audience: { total: 0, reachable: {} },
      audienceLoading: true,
      preview_dialog: false,
      sending: false,
      headers: [
        { text: "Customer", value: "name" },
        { text: "Channel", value: "channel" },
        { text: "Message", value: "title" },
        { text: "Status", value: "status" },
        { text: "Source", value: "trigger" },
        { text: "Sent", value: "sent_at" },
      ],
      channelFilterItems: CHANNELS.map((c) => ({ text: c.label, value: c.value })),
    };
  },
  computed: {
    channelLabel() {
      const c = CHANNELS.find((x) => x.value === this.form.channel);
      return c ? c.label : this.form.channel;
    },
    /**
     * How many channels this history actually used, and which.
     *
     * Before the KPI showed a hardcoded 4 with a hardcoded caption, which was
     * a statement about the platform rather than about the data on screen --
     * it read "4 / Push, WhatsApp, SMS, Email" even against an empty history.
     */
    channelsUsed() {
      return (this.summary.by_channel || []).length;
    },
    channelsUsedSub() {
      const used = (this.summary.by_channel || []).map((c) => c.label);
      return used.length ? used.join(", ") : "No sends yet";
    },
    isIndividual() {
      return this.form.mode === MODE.INDIVIDUAL;
    },
    /** The chosen people's ids, which is all the server needs. */
    recipientIds() {
      return this.recipients.map((r) => r.id);
    },
    /** Named people the current channel has no address for. */
    unreachableNames() {
      return this.recipients.filter((r) => r.reachable === false).map((r) => r.name);
    },
    /**
     * Named people the allowlist will silently drop.
     *
     * Only counted when they are otherwise reachable: someone with no email at
     * all is already reported by unreachableNames, and saying it twice reads
     * like two separate problems.
     */
    blockedNames() {
      return this.recipients
        .filter((r) => r.reachable !== false && r.allowlisted === false)
        .map((r) => r.name);
    },
    /**
     * Why the named people below cannot be reached, and what fixes it.
     *
     * "Cannot be reached on Push: Nalin Dewangan" is true but not actionable --
     * it does not say that push needs a registered device rather than a phone
     * number, which is the one thing that distinguishes it from the other three
     * channels. Each channel has exactly one reason to be unreachable, so the
     * reason can be stated rather than hinted at.
     */
    unreachableReason() {
      if (this.form.channel === CHANNEL.PUSH) {
        return "no registered device — push needs the app installed and signed in, not just a phone number.";
      }
      if (this.form.channel === CHANNEL.EMAIL) {
        return "no email address on their profile.";
      }
      // WhatsApp and SMS both need only a mobile number, so the only way to be
      // unreachable on either is not to have one.
      return "no mobile number on their profile.";
    },
    /**
     * The box stays a live search after the first pick, and says so.
     *
     * "Start typing to find a customer" is the wrong sentence once three people
     * are listed underneath it -- it reads as though nothing was found.
     */
    recipientPlaceholder() {
      return this.recipients.length
        ? "Add another person"
        : "Start typing to find a customer";
    },
    /**
     * The same fact as `unreachableReason`, in the width a list row has.
     *
     * The long form explains what would fix it and belongs in a tooltip or a
     * sentence; a row in a 465px column has space for two words.
     */
    unreachableShort() {
      if (this.form.channel === CHANNEL.PUSH) return "no device";
      if (this.form.channel === CHANNEL.EMAIL) return "no email";
      return "no mobile";
    },
    /**
     * How many of the named people will get nothing.
     *
     * The two lists never overlap -- `blockedNames` already excludes anyone
     * `unreachableNames` covers -- so they add rather than needing a union.
     */
    undeliverableCount() {
      return this.unreachableNames.length + this.blockedNames.length;
    },
    /** "Rahul Sharma" / "Rahul Sharma and Asha Verma" / "3 named people". */
    recipientSummary() {
      const names = this.recipients.map((r) => r.name);
      if (!names.length) return "nobody";
      if (names.length === 1) return names[0];
      if (names.length === 2) return `${names[0]} and ${names[1]}`;
      return `${names.length} named people`;
    },
    /** WhatsApp and SMS send a registered template; Push and Email send free text. */
    usesTemplate() {
      return this.form.channel === CHANNEL.WHATSAPP || this.form.channel === CHANNEL.SMS;
    },
    currentTemplates() {
      return this.templates[this.form.channel] || [];
    },
    templateItems() {
      return this.currentTemplates.map((t) => ({ text: t.label, value: t.name }));
    },
    templateHint() {
      if (this.form.channel === CHANNEL.SMS) {
        return "SMS bodies must be registered on the DLT portal — free text is rejected by the operator.";
      }
      return "The template must be approved by Meta before it will deliver.";
    },
    templateLabel() {
      const t = this.currentTemplates.find((x) => x.name === this.form.template);
      return t ? t.label : this.form.template;
    },
    reachable() {
      return (this.audience.reachable && this.audience.reachable[this.form.channel]) || 0;
    },
    reachGapNote() {
      if (this.form.channel === CHANNEL.PUSH) {
        return "Push only reaches users with the app installed, so the reachable count is lower.";
      }
      if (this.form.channel === CHANNEL.EMAIL) {
        return "Only users who shared an email address can be reached.";
      }
      return "Some users in this segment cannot be reached on this channel.";
    },
    canSend() {
      // A named selection nobody in it can receive is still not sendable, so
      // the reachable check applies to both modes -- but in individual mode an
      // empty picker is the more useful thing to block on.
      if (this.isIndividual && !this.recipients.length) return false;
      if (!this.reachable) return false;
      if (this.usesTemplate) return !!this.form.template;
      return !!this.form.title && !!this.form.body;
    },
  },
  watch: {
    /** Typing in the recipient box. `:search-input.sync` writes this. */
    recipientSearch(value) {
      if (this.isIndividual) this.onRecipientSearch(value);
    },
    /**
     * Switching between Segment and Individual.
     *
     * The counters describe whichever mode is showing, so they are re-resolved
     * on the way in -- otherwise a segment's total would sit above an empty
     * picker and read as though those people were about to be messaged.
     */
    "form.mode"() {
      this.loadAudience();
      if (this.isIndividual) this.refreshRecipientFlags();
    },
  },
  created() {
    this.load();
    this.loadUtmOptions();
    this.loadTemplates();

    // Arriving from a lead or customer detail page pre-targets that person.
    // Switching into Individual mode is what makes it visible: the id used to
    // be applied invisibly, so the screen showed the segment controls while
    // actually messaging one customer.
    const routeUserId = this.$route.query.user_id;
    if (routeUserId) {
      this.form.mode = MODE.INDIVIDUAL;
      this.form.bucket = null;
      this.loadRecipientFromRoute(routeUserId);
      // loadAudience is called by loadRecipientFromRoute once the person is
      // known -- calling it here too would ask for an empty selection first.
    } else {
      this.loadAudience();
    }
  },
  beforeDestroy() {
    // The debounce would otherwise fire into a destroyed component.
    clearTimeout(this.recipientTimer);
  },
  methods: {
    /** Imported helpers are not visible to templates; expose it explicitly. */
    displayLabel,
    load() {
      this.loading = true;
      this.error = null;
      api
        .getNotifications({
          page_number: this.page,
          page_length: this.pageLength,
          channel: this.filter_channel,
        })
        .then((data) => {
          this.items = data.results;
          this.summary = data.summary;
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
        })
        .catch((err) => {
          this.error = err;
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * Template lists from the server, replacing the bundled constants.
     *
     * Failure is not surfaced: the constants already hold the real registered
     * names for both channels, so falling back to them leaves Compose fully
     * usable rather than showing an error over a dropdown that would have worked.
     */
    loadTemplates() {
      api
        .getNotificationTemplates()
        .then((data) => {
          if (!data) return;
          // WhatsApp comes back as bare names; keep the friendly label from the
          // constant where there is one, and show an unrecognised name as itself.
          const whatsapp = data.whatsapp || [];
          if (whatsapp.length) {
            this.$set(
              this.templates,
              CHANNEL.WHATSAPP,
              whatsapp.map((name) => {
                const known = WHATSAPP_TEMPLATES.find((t) => t.name === name);
                return { name, label: known ? known.label : name };
              })
            );
          }
          // SMS already comes back as {name, label} -- dlt_keys.py has both.
          const sms = data.sms || [];
          if (sms.length) this.$set(this.templates, CHANNEL.SMS, sms);
          // The bundled constants are a fallback, not the truth, and most of
          // their WhatsApp names no longer match the registered ones -- so the
          // default picked from them can be a name the provider will reject.
          // The dropdown already shows the server's list; this stops the value
          // underneath it pointing at something that is not in it.
          this.ensureTemplateInList();
        })
        .catch(() => {});
    },
    loadAudience() {
      // In individual mode the segment filters are deliberately NOT sent: the
      // server ignores them when ids are present, and sending them anyway would
      // suggest on screen that they still narrow the selection.
      const params = this.isIndividual
        ? { user_ids: this.recipientIds }
        : {
            bucket: this.form.bucket,
            utm_source: this.form.utm_source,
            product_type: store.productTypeParam(),
          };

      // Nothing chosen yet: answer locally rather than asking the server for
      // the audience of an empty list, which would resolve to every customer.
      if (this.isIndividual && !this.recipientIds.length) {
        this.audience = { total: 0, reachable: {} };
        this.audienceLoading = false;
        return;
      }

      this.audienceLoading = true;
      api
        .getNotificationAudience(params)
        .then((data) => {
          this.audience = data;
        })
        .catch(() => {
          this.audience = { total: 0, reachable: {} };
        })
        .finally(() => {
          this.audienceLoading = false;
        });
    },
    loadUtmOptions() {
      if (store.utmOptions) {
        this.utmSources = store.utmOptions.utm_sources;
        return;
      }
      api
        .getUtmOptions()
        .then((opts) => {
          store.setUtmOptions(opts);
          this.utmSources = opts.utm_sources;
        })
        .catch(() => {
          this.utmSources = [];
        });
    },
    reload() {
      this.page = 1;
      this.load();
    },
    onPage(page) {
      this.page = page;
      this.load();
    },
    /**
     * Look customers up as the operator types.
     *
     * Debounced rather than fired per keystroke: the search runs a LIKE over
     * name, mobile and email, and the picker in AssignAgentDialog -- which does
     * fire on every @keyup -- is the thing this deliberately does not copy.
     * 350 ms is the interval FilterBar already uses for its search box.
     */
    onRecipientSearch(query) {
      clearTimeout(this.recipientTimer);
      const term = (query || "").trim();
      if (!term) {
        this.recipientItems = this.recipients.slice();
        this.recipientLoading = false;
        return;
      }
      this.recipientLoading = true;
      this.recipientTimer = setTimeout(() => {
        api
          .searchNotificationRecipients({
            search_query: term,
            channel: this.form.channel,
          })
          .then((rows) => {
            // Already-chosen people are kept in the item list. v-autocomplete
            // renders a chip from `items`, so dropping them here would blank
            // every existing chip the moment a new search came back.
            const chosen = this.recipients.filter(
              (r) => !rows.some((row) => row.id === r.id)
            );
            this.recipientItems = chosen.concat(rows);
          })
          .catch(() => {
            this.recipientItems = this.recipients.slice();
          })
          .finally(() => {
            this.recipientLoading = false;
          });
      }, 350);
    },
    onRecipientsChange() {
      this.loadAudience();
    },
    /**
     * Drop one person from the selection.
     *
     * Goes through the same handler the autocomplete's own @change uses, so the
     * audience count is reloaded whichever way someone was removed. The row is
     * deliberately left in `recipientItems`: that list feeds the dropdown, and
     * removing it there too would take the person out of the search results
     * they were just picked from.
     */
    removeRecipient(recipient) {
      this.recipients = this.recipients.filter((r) => r.id !== recipient.id);
      this.onRecipientsChange();
    },
    clearRecipients() {
      this.recipients = [];
      this.onRecipientsChange();
    },
    /**
     * What to print under a chosen person's name.
     *
     * The mobile, except on Email -- where the address is the thing that
     * decides whether the message lands. Push resolves to a device token, which
     * is 160 characters of noise, so the mobile identifies the person there
     * too. Blank when `display_name` already fell back to the mobile, which
     * would otherwise print the same number twice.
     */
    recipientContact(recipient) {
      if (this.form.channel === CHANNEL.EMAIL) return recipient.email || "";
      return recipient.mobile === recipient.name ? "" : recipient.mobile;
    },
    /** Why this person will receive nothing, or "" when they will. */
    recipientProblem(recipient) {
      if (recipient.reachable === false) return this.unreachableShort;
      // Only asked once reachability passes: someone with no address at all is
      // already reported above, and saying both reads as two separate faults.
      if (recipient.allowlisted === false) return "not allowlisted";
      return "";
    },
    /** Drop a template selection the current channel's list does not contain. */
    ensureTemplateInList() {
      if (!this.usesTemplate) return;
      const names = this.currentTemplates.map((t) => t.name);
      if (!names.includes(this.form.template)) {
        this.form.template = names.length ? names[0] : null;
      }
    },
    /**
     * Re-read the chosen people's flags for the current channel.
     *
     * `reachable` and `allowlisted` are both per channel -- someone reachable
     * by SMS may have no device token and no email -- so without this the
     * warnings would keep describing the channel they were first fetched for.
     */
    refreshRecipientFlags() {
      if (!this.recipientIds.length) return;
      api
        .searchNotificationRecipients({
          user_ids: this.recipientIds,
          channel: this.form.channel,
        })
        .then((rows) => {
          if (!rows.length) return;
          this.recipients = this.recipients.map(
            (r) => rows.find((row) => row.id === r.id) || r
          );
          this.recipientItems = this.recipients.slice();
        })
        .catch(() => {});
    },
    /**
     * Seed the picker from ?user_id=, the link the lead and customer screens use.
     *
     * The id alone is enough for the server, but not for the operator: without
     * this the screen targeted one person while still showing the segment
     * controls, so nothing on screen said who was about to be messaged.
     */
    loadRecipientFromRoute(userId) {
      api
        .searchNotificationRecipients({
          user_ids: [userId],
          channel: this.form.channel,
        })
        .then((rows) => {
          if (!rows.length) return;
          this.recipients = rows;
          this.recipientItems = rows.slice();
          this.loadAudience();
        })
        .catch(() => {});
    },
    selectChannel(value) {
      this.form.channel = value;
      // The counters need no refetch -- the audience response carries all four
      // channels and `reachable` just reads a different key. The per-person
      // warnings do: their two flags were resolved for the old channel.
      if (this.isIndividual) this.refreshRecipientFlags();
      // A template name belongs to one channel only, so carrying the WhatsApp
      // selection into SMS would send a name the DLT register has never seen.
      this.ensureTemplateInList();
    },
    openPreview() {
      this.preview_dialog = true;
    },
    send() {
      this.sending = true;
      api
        .sendNotification({
          channel: this.form.channel,
          template: this.form.template,
          title: this.form.title,
          body: this.form.body,
          // The whole segment travels, not just the bucket. The server
          // re-resolves it to decide who actually receives the message, so
          // omitting utm_source / product_type / user_ids -- as this used to --
          // meant it resolved a WIDER audience than the one just previewed.
          //
          // Individual mode sends ONLY the ids, matching what loadAudience
          // previewed: a bucket sent alongside them would be ignored by the
          // server but would still be the wrong thing to have asked for.
          ...(this.isIndividual
            ? { user_ids: this.recipientIds }
            : {
                bucket: this.form.bucket,
                utm_source: this.form.utm_source,
                product_type: store.productTypeParam(),
              }),
          audience_size: this.reachable,
        })
        .then((res) => {
          this.preview_dialog = false;
          const receipt = this.sendReceipt(res);
          this.showSnakeBar(receipt.type, receipt.text);
          // The sends land in the history table this screen is showing. They are
          // dispatched in the background, so a row may not be there yet -- this
          // catches the fast ones and costs nothing when it does not.
          this.reload();
          this.loadAudience();
        })
        .catch((err) => {
          this.showSnakeBar("error", this.getApiErrorMessage(err) || "Could not send");
        })
        .finally(() => {
          this.sending = false;
        });
    },
    /**
     * What actually happened, as {type, text} for the snackbar.
     *
     * The severity is part of the answer, not decoration. A send where the
     * allowlist matched nobody returns HTTP 200 -- correctly, it is not an error
     * -- and reporting that in green as though a campaign went out is the one
     * outcome an operator must not misread. Green only when something is queued.
     *
     * `skipped` is shown whenever it is non-zero rather than hidden: an operator
     * who sees "Sending to 12 users" after targeting a segment of four thousand
     * needs to know the other 3,988 were filtered by the allowlist.
     */
    sendReceipt(res) {
      if (res.simulated) {
        return {
          type: "warning",
          text: `Preview complete — ${fmt.number(res.queued)} users would be notified`,
        };
      }
      const queued = res.queued || 0;
      const skipped = res.skipped || 0;
      if (!queued) {
        return {
          type: "warning",
          text: skipped
            ? `Nothing sent — all ${fmt.number(skipped)} recipients are outside the test allowlist`
            : "Nothing sent — no one in this segment is reachable on this channel",
        };
      }
      const sent = `Sending to ${fmt.number(queued)} user${queued === 1 ? "" : "s"}`;
      return {
        type: "success",
        text: skipped
          ? `${sent} — ${fmt.number(skipped)} skipped (not on the test allowlist)`
          : sent,
      };
    },
    channelIcon(channel) {
      const c = CHANNELS.find((x) => x.value === channel);
      return c ? c.icon : "bell";
    },
    channelColor(channel) {
      return CHANNEL_COLORS[channel] || "#607D8B";
    },
    statusWidth(value) {
      const max = this.summary.by_status.reduce((m, s) => Math.max(m, s.value), 0);
      if (!max) return "0%";
      return Math.max(3, (value / max) * 100) + "%";
    },
  },
};
</script>

<style scoped>
.kf-note {
  font-size: var(--kf-fs-meta) !important;
  line-height: 1.55;
  margin-bottom: 14px;
}
.kf-card {
  background: var(--kf-surface);
  border-radius: 10px;
  padding: 14px 16px;
}
.kf-card__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--kf-brand);
}
.kf-section {
  font-size: var(--kf-fs-label);
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 14px 0 7px;
}
.kf-channels {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 7px;
}
.kf-channel {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 11px;
  border-radius: 8px;
  border: 1px solid var(--kf-border);
  background: var(--kf-surface-2);
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #000;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}
/* Same specificity trap as the User Management tabs: `:hover` (0,2,0) beat
   `--active` (0,1,0) on background while the white text survived, so hovering
   the selected channel hid the label -- and the icon too, since its colour is
   bound to white when selected. */
.kf-channel:hover:not(.kf-channel--active) {
  background: var(--kf-hover);
  color: #000;
  border-color: var(--kf-border-strong);
}
.kf-channel--active {
  background: var(--kf-brand);
  border-color: var(--kf-brand);
  color: #fff;
}
.kf-channel--active:hover {
  background: var(--kf-brand-strong);
  border-color: var(--kf-brand-strong);
}
.kf-hint-sm {
  font-size: var(--kf-fs-label);
  color: #000;
  margin-top: 6px;
}
/* Full width so the two halves split evenly -- v-btn-toggle otherwise sizes
   each button to its own label and "Segment"/"Individual" come out uneven. */
.kf-mode {
  display: flex;
  width: 100%;
  border: 1px solid var(--kf-border);
  border-radius: 8px;
  overflow: hidden;
}
.kf-mode >>> .v-btn {
  flex: 1 1 0;
  font-size: var(--kf-fs-label);
}
/* Vuetify blanks the placeholder of any select that has a value
   (`.v-select.v-input--is-dirty ::placeholder { color: transparent !important }`)
   because the chosen value normally occupies that space. Here it does not --
   the selection is listed below the field -- so the rule would leave an empty
   white box behind a label, with nothing saying it is still a search. */
.kf-recip-search >>> input::placeholder {
  color: rgba(0, 0, 0, 0.38) !important;
}

/* --------------------------------------------------- chosen recipients --- */
/* Deliberately NOT chips inside the search field. A 32px chip in a 40px dense
   box overlaps the floating label that sits 9px into the field, and a chip has
   nowhere to put the mobile number that tells two same-named customers apart.
   MultiSelect.vue made the same call for the filter bar, for the same reason:
   in-field chip lists wrap and grow the panel taller than the content. */
.kf-recips {
  border: 1px solid var(--kf-border);
  border-radius: 8px;
  background: var(--kf-surface-2);
  overflow: hidden;
}
.kf-recips__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--kf-border);
  font-size: var(--kf-fs-label);
  font-weight: 700;
  color: #000;
}
.kf-recips__clear {
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: var(--kf-brand);
  cursor: pointer;
  outline: none;
}
.kf-recips__clear:hover {
  text-decoration: underline;
}
/* Four rows, then scroll: ten named people used to push Send off the screen. */
.kf-recips__list {
  max-height: 176px;
  overflow-y: auto;
}
.kf-recips__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-top: 1px solid var(--kf-border);
}
.kf-recips__row:first-child {
  border-top: none;
}
.kf-recips__row--warn {
  background: #fff8e1;
}
.kf-recips__who {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.kf-recips__name {
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kf-recips__contact {
  font-size: var(--kf-fs-label);
  color: #000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kf-recips__status {
  flex: 0 0 auto;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: #ef6c00;
  white-space: nowrap;
}
.kf-recips__remove {
  flex: 0 0 auto;
  display: inline-flex;
  padding: 3px;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
}
.kf-recips__remove:hover {
  background: var(--kf-hover);
}
.kf-audience {
  background: var(--kf-surface-2);
  border: 1px solid var(--kf-border);
  border-radius: 8px;
  padding: 9px 11px;
  margin-top: 4px;
}
.kf-audience__row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  font-size: var(--kf-fs-meta);
  color: #000;
  padding: 2px 0;
}
.kf-audience__row strong {
  color: var(--kf-brand);
  font-size: 15px;
}
.kf-audience__row--sub strong {
  color: #00897b;
}
.kf-audience__note {
  font-size: var(--kf-fs-label);
  color: #000;
  margin-top: 4px;
  line-height: 1.45;
}
.kf-breakdown {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}
.kf-bd__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
}
.kf-bd__value {
  font-size: var(--kf-fs-hint);
  font-weight: 700;
  color: var(--kf-brand);
}
.kf-bd__bar {
  height: 5px;
  border-radius: 4px;
  background: var(--kf-hover);
  overflow: hidden;
}
.kf-bd__fill {
  height: 100%;
  border-radius: 4px;
}
.kf-inline-select {
  max-width: 150px;
}
.kf-ch {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--kf-fs-meta);
  white-space: nowrap;
}
.kf-user__name {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
  white-space: nowrap;
}
.kf-user__sub {
  font-size: var(--kf-fs-label);
  color: #000;
}
.kf-auto {
  font-size: var(--kf-fs-label);
  color: #7b1fa2;
  font-weight: 600;
  text-transform: capitalize;
}
.kf-preview {
  border: 1px solid var(--kf-border);
  border-radius: 8px;
  overflow: hidden;
}
.kf-preview__head {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--kf-surface-2);
  padding: 7px 11px;
  font-size: var(--kf-fs-label);
  font-weight: 700;
  color: #000;
  border-bottom: 1px solid var(--kf-border);
}
.kf-preview__body {
  padding: 11px;
  background: #fff;
}
.kf-preview__title {
  font-size: var(--kf-fs-hint);
  font-weight: 700;
  color: #000;
}
.kf-preview__text {
  font-size: var(--kf-fs-meta);
  color: #000;
  margin-top: 3px;
  line-height: 1.5;
}
.kf-preview__text code {
  font-size: var(--kf-fs-label);
  background: var(--kf-surface);
  padding: 0 4px;
  border-radius: 3px;
}
.kf-warn {
  font-size: var(--kf-fs-meta);
  color: #ef6c00;
  font-weight: 600;
}
.kf-muted {
  color: #000;
}
</style>
